<script setup lang="ts">
import {ref, watch, onMounted, computed} from "vue";
import {useI18n} from 'vue-i18n';
import {useSearchStore} from "@/stores/searchStore";
import {boolean, string} from "yup";
import DestinationPill from "@/components/Destination/DestinationPill.vue";
import { useDebounceFn } from '@vueuse/core';

const props = defineProps({
  'icon':{
    type:boolean,
    default:true
  },
  'class':{
    type:string
  },
  maxVisible: {
    type: Number, 
    default: null
  },
  prefillLabel: {
    type: String,
    default: ''
  },  locked: {
    type: Boolean,
    default: false
  },
  fromTripAddModal: {
    type: Boolean,
    default: false,
  }
})

const model = defineModel<any[]>()
const {t} = useI18n(); // Initialize the i18n function
const searchStore = useSearchStore()
const open = ref(false)
const query = ref('')
const selected = ref(false);

function syncQueryFromModel() {
  if (!model.value || model.value.length <= 0) {
    return;
  }
  const found = searchStore.filters.destinations.find((d) => d.value == model.value[0]);
  // Prefer label from store; fallback to provided prefill label if available
  if (found?.label) {
    query.value = found.label;
  } else if (props.prefillLabel) {
    query.value = props.prefillLabel;
  }
  selected.value = true;
  open.value = false;
}

function onDestinationClick(value) {
  model.value = [value.value];
  selected.value = true;
  open.value = false;
  query.value=value.label
}

const debouncedSearch = useDebounceFn((newValue: string) => {
  if (props.locked) return;
  searchStore.searchDestinations(newValue);
}, 300);

watch(query, (newValue) => {
  debouncedSearch(newValue);
})

watch(model, () => {
  syncQueryFromModel();
});

onMounted(() => {
  syncQueryFromModel();
});

// Watch for prefillLabel changes, and set query if nothing is set yet
watch(() => props.prefillLabel, (lbl) => {
  if (!lbl) return;
  if (!query.value) {
    syncQueryFromModel();
  }
});

watch(() => props.locked, (locked) => {
  if (locked) {
    if (props.prefillLabel && !query.value) {
      syncQueryFromModel();
    }
    selected.value = true;
    open.value = false;
  }
});

// Robust helper to detect harbours
function isHarbour(d: any) {
  const direct = (d?.entity ?? d?.type ?? d?.kind ?? '').toString().trim().toLowerCase();
  const nested = (typeof d?.entity === 'object' ? (d?.entity?.name ?? '') : '').toString().trim().toLowerCase();
  return direct === 'harbour' || direct === 'harbor' || nested === 'harbour' || nested === 'harbor';
}

const limitedDestinations = computed(() => {
  let destinations = Array.isArray(searchStore.filters.destinations)
    ? searchStore.filters.destinations
    : [];

  if (props.fromTripAddModal) {
    const before = destinations.length;
    destinations = destinations.filter(isHarbour);
    // Debug: show why nothing appears
    // eslint-disable-next-line no-console
    console.log('[DestinationInput] fromTripAddModal=true → filtering to Harbour only. Before:', before, 'After:', destinations.length, 'Sample:', destinations[0]);
  }

  if (props.maxVisible && destinations.length > props.maxVisible) {
    return destinations.slice(0, props.maxVisible);
  }
  return destinations;
});
</script>

<template>
  <div class="relative flex">
    <VTextField :placeholder="t('destination')"
                variant="underlined"
                v-model="query"
                :class="[props.class, 'select-none']"
                :readonly="props.locked"
                :tabindex="props.locked ? -1 : 0"
                @click="open = !props.locked">
      <template #prepend-inner>
        <img src="@/assets/icons/svg/Location_16x16.svg" width="16" height="16" :alt="t('destination')"
             v-if="props.icon"
             class="min-w-5"/>
      </template>
    </VTextField>
    <div v-if="open && !props.locked"
         class="px-6 py-4  w-full absolute top-[55%] bg-white shadow-2xl h-fit min-w-[20rem] max-h-[22rem] overflow-y-scroll z-30">
      <div>
        <div v-if="searchStore.filters.destinations.length === 0">
          <div class="result transition-all flex flex-row items-center justify-between">
            <div>
              <span class="">{{ t('search.startTyping') }}</span>
            </div>
          </div>
        </div>
        <div v-else>
          <div v-if="limitedDestinations.length === 0 && props.fromTripAddModal" class="result transition-all flex flex-row items-center justify-between">
            <div>
              <span>No harbours found for your search.</span>
            </div>
          </div>
          <div v-else>
            <div v-for="rec in limitedDestinations" :key="rec.id"
                 class="destination-item flex flex-row items-center justify-between gap-2 cursor-pointer"
                 @click="onDestinationClick(rec)">
              <div class="line-clamp-1 text-ellipsis">
                <span class="">{{ rec.label }}</span>
              </div>
              <DestinationPill :destination="rec"/>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.destination-item:not(:last-child) {
  margin-bottom: .75rem;
}
.v-text-field--underlined.v-input--is-disabled input,
.v-text-field--underlined.v-input--is-readonly input {
  color: black !important;
}
</style>